import React from 'react';
import type { MessageThread } from '../../types/message';
import { formatRelativeTime } from '../../utils/dateUtils';
import { useLanguage } from '../../i18n/useLanguage';
import { UserAvatar } from '../common/UserAvatar';
import './ThreadList.css';

interface ThreadListProps {
  threads: MessageThread[];
  currentThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  onDeleteThread?: (threadId: string) => void;
  isLoading?: boolean;
}

export const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  currentThreadId,
  onSelectThread,
  onDeleteThread,
  isLoading = false,
}) => {
  const { language, t } = useLanguage();
  if (isLoading && threads.length === 0) {
    return (
      <div className="thread-list">
        <div className="thread-list-loading">{t('messages.threadList.loading')}</div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="thread-list">
        <div className="thread-list-empty">{t('messages.threadList.empty')}</div>
      </div>
    );
  }

  const handleDelete = (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    if (onDeleteThread && window.confirm(t('messages.delete.confirm'))) {
      onDeleteThread(threadId);
    }
  };

  return (
    <div className="thread-list">
      {threads.map((thread) => (
        <div
          key={thread.id}
          className={`thread-item ${currentThreadId === thread.id ? 'thread-item--active' : ''} ${thread.unreadCount > 0 ? 'thread-item--unread' : ''}`}
          onClick={() => onSelectThread(thread.id)}
        >
          <UserAvatar
            userId={thread.otherParticipantId}
            username={thread.otherParticipantUsername}
            size={48}
            className="thread-item-avatar"
          />
          <div className="thread-item-content">
            <div className="thread-item-header">
              <div className="thread-item-participant">{thread.otherParticipantUsername}</div>
              {thread.unreadCount > 0 && (
                <span className="thread-item-unread-badge">{thread.unreadCount}</span>
              )}
            </div>
            <div className="thread-item-footer">
              {thread.lastMessagePreview && thread.lastMessagePreview.trim() ? (
                <div className="thread-item-preview">{thread.lastMessagePreview}</div>
              ) : thread.lastMessageAt ? (
                <div className="thread-item-preview"></div>
              ) : (
                <div className="thread-item-preview">{t('messages.threadList.noPreview')}</div>
              )}
              {thread.lastMessageAt && (
                <div className="thread-item-time" title={thread.lastMessageAt}>
                  {formatRelativeTime(thread.lastMessageAt, language)}
                </div>
              )}
            </div>
          </div>
          {onDeleteThread && (
            <button
              type="button"
              className="thread-item-delete"
              onClick={(e) => handleDelete(e, thread.id)}
              title={t('messages.delete.tooltip')}
              aria-label={t('messages.delete.aria')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
